import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import client from "@/api/client";
import { Progress } from "@/components/ui/progress";
import { Loader2, KeyRound, PencilLine, UserCog, MapPin, ArrowRight, CalendarClock, ClipboardList, Users, Bell, IdCard, Building2, Target, FolderLock, Compass, LifeBuoy, SquarePen } from "lucide-react";
import { AdminUserEditForm } from "@/components/admin/AdminUserEditForm";
import { format, parseISO, formatDistanceToNow } from "date-fns";
import { ar as arLocale } from "date-fns/locale";

const TABS = ["overview", "edit", "profile", "program", "performed", "received", "sign_ins", "bookings", "tasks", "rsvps", "notifications"];
const MEMBER_TABS = ["bookings", "tasks", "rsvps", "notifications"];

let dfLocale;
function rel(iso) {
  try { return formatDistanceToNow(parseISO(iso), { addSuffix: true, locale: dfLocale }); } catch { return ""; }
}
function abs(iso) {
  try { return format(parseISO(iso), "MMM d, yyyy · HH:mm", { locale: dfLocale }); } catch { return iso || ""; }
}
function shortValue(v) {
  if (v === null || v === undefined || v === "") return "—";
  const s = String(v);
  return s.length > 40 ? `${s.slice(0, 40)}…` : s;
}

function AuditRow({ row, t }) {
  // Same `entity.verb` → localized label mapping as the Change log.
  const raw = String(row.action || "");
  const i = raw.lastIndexOf(".");
  const label = i > 0
    ? `${t(`auditEntities.${raw.slice(0, i)}`, { defaultValue: raw.slice(0, i).replace(/_/g, " ") })} · ${t(`auditVerbs.${raw.slice(i + 1)}`, { defaultValue: raw.slice(i + 1).replace(/_/g, " ") })}`
    : raw.replace(/[._]/g, " ");
  return (
    <li className="border-s-2 border-primary/30 ps-3 py-1.5" data-testid={`user-activity-audit-${row.audit_id}`}>
      <p className="text-xs font-medium">{label}</p>
      <p className="text-[11px] text-muted-foreground truncate">
        {row.entity_label || row.entity_id || row.entity_type} · {rel(row.created_at)}
      </p>
      {(row.changes || []).length > 0 && (
        <div className="flex flex-wrap gap-1 mt-1">
          {row.changes.slice(0, 4).map((c, i) => (
            <span key={`${c.field}-${i}`} className="inline-flex items-center gap-1 rounded-md bg-muted/60 px-1.5 py-0.5 text-[10px] font-mono">
              {c.field}
              <span className="line-through opacity-60">{shortValue(c.from)}</span>
              <ArrowRight className="h-2.5 w-2.5 text-primary rtl-flip" />
              <span className="font-semibold">{shortValue(c.to)}</span>
            </span>
          ))}
        </div>
      )}
    </li>
  );
}

/**
 * AdminUserActivityDialog — one account's full paper trail: their sign-ins,
 * the changes they made, and the changes admins made to them.
 */
export function AdminUserActivityDialog({ open, onOpenChange, user }) {
  const { t, i18n } = useTranslation();
  const isAr = i18n.language === "ar";
  dfLocale = i18n.language === "ar" ? arLocale : undefined;
  const [tab, setTab] = useState("overview");
  const [detail, setDetail] = useState(null);
  const [data, setData] = useState(null);
  const [member, setMember] = useState(null);
  const [businessTypes, setBusinessTypes] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    if (!user?.user_id) return;
    setLoading(true);
    setData(null);
    setMember(null);
    setDetail(null);
    try {
      const res = await client.get(`/admin/activity/user/${user.user_id}`);
      setData(res.data);
    } catch (e) {
      setData(null);
    }
    try {
      const res = await client.get(`/admin/members/${user.user_id}/activity`);
      setMember(res.data);
    } catch (e) {
      setMember(null);
    }
    try {
      const res = await client.get(`/admin/users/${user.user_id}`);
      setDetail(res.data);
    } catch (e) {
      setDetail(null);
    } finally {
      setLoading(false);
    }
  }, [user?.user_id]);

  useEffect(() => {
    if (open) { setTab("overview"); load(); }
  }, [open, load]);

  useEffect(() => {
    if (!open || businessTypes.length) return;
    client.get("/business-types")
      .then((res) => setBusinessTypes(res.data?.items || res.data || []))
      .catch(() => setBusinessTypes([]));
  }, [open, businessTypes.length]);

  const labels = {
    overview: t("adminUserView.tabOverview", { defaultValue: "Overview" }),
    edit: t("adminUserEdit.tab", { defaultValue: "Edit details" }),
    profile: t("adminUserView.tabProfile", { defaultValue: "Profile details" }),
    program: t("adminUserView.tabProgram", { defaultValue: "Program" }),
    performed: t("adminActivity.tabPerformed", { defaultValue: "Changes made" }),
    received: t("adminActivity.tabReceived", { defaultValue: "Changes to them" }),
    sign_ins: t("adminActivity.tabSignIns", { defaultValue: "Sign-ins" }),
    bookings: t("memberActivity.feed.bookings", { defaultValue: "Sessions" }),
    tasks: t("memberActivity.feed.tasks", { defaultValue: "Tasks" }),
    rsvps: t("memberActivity.feed.rsvps", { defaultValue: "Event RSVPs" }),
    notifications: t("memberActivity.feed.notifications", { defaultValue: "Alerts sent" }),
  };
  const icons = {
    overview: IdCard, profile: Compass, program: Target, edit: SquarePen,
    performed: PencilLine, received: UserCog, sign_ins: KeyRound,
    bookings: CalendarClock, tasks: ClipboardList, rsvps: Users, notifications: Bell,
  };
  const counts = { ...(data?.counts || {}), ...(member?.counts || {}) };
  const bt = user?.business_type;

  const renderMemberRows = (key) => {
    const rows = member?.[key] || [];
    if (rows.length === 0) {
      return (
        <p className="text-sm text-muted-foreground py-6 text-center">
          {t("memberActivity.empty", { defaultValue: "Nothing recorded for this filter." })}
        </p>
      );
    }
    return (
      <ul className="space-y-1.5 max-h-[50vh] overflow-y-auto" data-testid={`user-activity-${key}`}>
        {rows.map((r, idx) => {
          const id = r.booking_id || r.task_id || r.rsvp_id || r.notification_id || idx;
          const title =
            key === "bookings"
              ? `${r.direction === "hosting" ? t("memberActivity.hosting", { defaultValue: "Hosting" }) : t("memberActivity.requested", { defaultValue: "Requested" })} · ${r.direction === "hosting" ? (r.requester_name || "—") : (r.mentor_name || "—")}`
              : key === "tasks" ? r.title
              : key === "rsvps" ? r.event_title
              : r.content;
          const stamp = key === "bookings" ? r.start_time : r.created_at;
          return (
            <li key={id} className="rounded-xl border border-border p-2.5" data-testid={`user-activity-${key}-${id}`}>
              <div className="flex items-start gap-2">
                <p className="text-xs font-medium min-w-0 flex-1 truncate">{title || "—"}</p>
                {r.status && <Badge variant="outline" className="text-[10px] capitalize shrink-0">{String(r.status).replace(/_/g, " ")}</Badge>}
              </div>
              <p className="text-[11px] text-muted-foreground mt-0.5 font-mono">
                {abs(stamp)}
                {r.due_at ? ` · due ${r.due_at.slice(0, 10)}` : ""}
              </p>
              {(r.cancel_request || {}).status === "pending" && (
                <p className="text-[11px] text-amber-700 mt-1">
                  {t("memberActivity.cancelPending", { defaultValue: "cancel pending" })}
                  {r.cancel_request.reason ? ` — “${r.cancel_request.reason}”` : ""}
                </p>
              )}
            </li>
          );
        })}
      </ul>
    );
  };

  const Facts = ({ rows, testId }) => (
    <dl className="grid gap-2 sm:grid-cols-2" data-testid={testId}>
      {rows.filter(([, v]) => v !== undefined).map(([label, value]) => (
        <div key={label} className="min-w-0 rounded-xl border border-border p-2.5">
          <dt className="text-[10px] uppercase tracking-widest text-muted-foreground truncate">{label}</dt>
          <dd className="text-xs mt-0.5 break-words">{value || "—"}</dd>
        </div>
      ))}
    </dl>
  );

  const list = (arr) => (Array.isArray(arr) && arr.length ? arr.join(", ") : "");

  const renderOverview = () => {
    const u = detail?.user || user || {};
    const c = detail?.company;
    const rooms = detail?.data_rooms || [];
    return (
      <div className="space-y-3 max-h-[52vh] overflow-y-auto pe-1" data-testid="user-view-overview">
        <Facts
          testId="user-view-identity"
          rows={[
            [t("usersManager.colUser"), u.name],
            [t("usersManager.colEmail"), u.email],
            [t("usersManager.colPhone"), u.phone],
            [t("roles.label", { defaultValue: "Role" }), t(`roles.${u.role}`, { defaultValue: u.role })],
            [t("usersManager.colStatus"), t(`usersManager.${u.status || "approved"}`, { defaultValue: u.status || "approved" })],
            [t("adminUserView.joined", { defaultValue: "Joined" }), u.created_at ? abs(u.created_at) : ""],
            [t("adminUserView.lastLogin", { defaultValue: "Last sign-in" }), u.last_login_at ? abs(u.last_login_at) : ""],
            [t("usersManager.colBusinessType", { defaultValue: "Business type" }), bt ? (isAr && bt.name_ar ? bt.name_ar : bt.name_en) : ""],
          ]}
        />

        <div className="rounded-xl border border-border p-3" data-testid="user-view-business">
          <p className="text-[11px] font-semibold flex items-center gap-1.5 mb-2">
            <Building2 className="h-3.5 w-3.5 text-primary" />
            {t("adminUserView.business", { defaultValue: "Business" })}
          </p>
          {c ? (
            <Facts
              rows={[
                [t("dashboard.companyTitle"), c.name],
                [t("onboarding.sectors"), c.industry],
                [t("onboarding.region"), c.region],
                [t("onboarding.stage"), c.stage],
              ]}
            />
          ) : (
            <p className="text-xs text-muted-foreground">{t("adminUserView.noCompany", { defaultValue: "No company linked yet." })}</p>
          )}
        </div>

        <div className="rounded-xl border border-border p-3" data-testid="user-view-rooms">
          <p className="text-[11px] font-semibold flex items-center gap-1.5 mb-2">
            <FolderLock className="h-3.5 w-3.5 text-primary" />
            {t("dataRoom.navTitle", { defaultValue: "Data rooms" })} · {rooms.length}
          </p>
          {rooms.length === 0 ? (
            <p className="text-xs text-muted-foreground">—</p>
          ) : (
            <div className="flex flex-wrap gap-1.5">
              {rooms.map((r) => (
                <Badge key={r.room_id} variant="outline" className="rounded-full text-[10px]">
                  {r.company_name || r.room_id} · {r.role}
                </Badge>
              ))}
            </div>
          )}
        </div>

        {detail?.tickets && (
          <p className="text-[11px] text-muted-foreground flex items-center gap-1.5" data-testid="user-view-tickets">
            <LifeBuoy className="h-3.5 w-3.5" />
            {t("dashboard.tickets")}: {detail.tickets.total} · {t("memberTasks.filter.open", { defaultValue: "Open" })} {detail.tickets.open}
          </p>
        )}
      </div>
    );
  };

  const renderProfile = () => {
    const pr = detail?.profile || {};
    const tpls = detail?.journey_templates || [];
    const hasProfile = Object.keys(pr).some(
      (k) => !["profile_id", "user_id", "role", "created_at", "updated_at"].includes(k)
        && pr[k] !== "" && pr[k] !== null && !(Array.isArray(pr[k]) && pr[k].length === 0),
    );
    const hasJourney = tpls.length > 0 || !!detail?.journey_notes;
    if (!hasProfile && !hasJourney) {
      return (
        <p className="text-sm text-muted-foreground py-8 text-center" data-testid="user-view-profile-empty">
          {t("adminUserView.noProfile", { defaultValue: "This user hasn't filled in a profile yet." })}
        </p>
      );
    }
    return (
      <div className="space-y-3 max-h-[52vh] overflow-y-auto pe-1" data-testid="user-view-profile">
        <Facts
          rows={[
            [t("onboarding.headline"), pr.headline],
            [t("profileFields.country", { defaultValue: "Country" }), pr.country || pr.region],
            [t("onboarding.stage"), pr.stage],
            [t("onboarding.sectors"), list(pr.sectors)],
            [t("onboarding.expertise"), list(pr.expertise)],
            [t("onboarding.yearsExperience"), pr.years_experience],
            [t("onboarding.serviceType"), pr.service_type],
            [t("onboarding.servicesOffered"), list(pr.services)],
            [t("onboarding.investorFocus"), list(pr.investor_focus)],
            [t("onboarding.ticketSize"), pr.ticket_size],
            [t("onboarding.website"), pr.website],
            [t("onboarding.linkedinUrl"), pr.linkedin_url],
            [t("onboarding.pitchDocUrl"), pr.pitch_doc_url],
          ].filter(([, v]) => v !== undefined && v !== "" && v !== null)}
        />
        {pr.bio && (
          <div className="rounded-xl border border-border p-3">
            <p className="text-[10px] uppercase tracking-widest text-muted-foreground">{t("onboarding.bio")}</p>
            <p className="text-xs mt-1 whitespace-pre-wrap">{pr.bio}</p>
          </div>
        )}
        {hasJourney && (
        <div className="rounded-xl border border-border p-3" data-testid="user-view-journey">
          <p className="text-[11px] font-semibold mb-2">
            {t("adminUserView.journey", { defaultValue: "Journey selections" })} · {tpls.length}
          </p>
          {tpls.length === 0 ? null : (
            <div className="flex flex-wrap gap-1.5">
              {tpls.map((tp) => (
                <Badge key={tp.template_id} variant="outline" className="rounded-full text-[10px]">
                  {isAr && tp.title_ar ? tp.title_ar : tp.title_en}
                  {tp.milestone_count > 0 && <span className="font-mono opacity-70 ms-1">· {tp.milestone_count}</span>}
                </Badge>
              ))}
            </div>
          )}
          {detail?.journey_notes && <p className="text-xs mt-2 whitespace-pre-wrap">{detail.journey_notes}</p>}
        </div>
        )}
      </div>
    );
  };

  const renderProgram = () => {
    const pg = detail?.program;
    if (!pg) {
      return (
        <p className="text-sm text-muted-foreground py-8 text-center" data-testid="user-view-program-empty">
          {t("adminUserView.noProgram", { defaultValue: "This user's company is not enrolled in a program." })}
        </p>
      );
    }
    const ms = pg.milestones || [];
    return (
      <div className="space-y-3 max-h-[52vh] overflow-y-auto pe-1" data-testid="user-view-program">
        <div className="rounded-xl border border-border p-3 space-y-2">
          <p className="text-sm font-semibold">{isAr && pg.name_ar ? pg.name_ar : pg.name}</p>
          <div className="flex items-center justify-between text-[11px] text-muted-foreground">
            <span>{t("milestones.progress", { defaultValue: "Progress" })}</span>
            <span className="font-mono" data-testid="user-view-program-progress">
              {pg.milestones_done}/{pg.milestones_total} · {pg.completion_pct}%
            </span>
          </div>
          <Progress value={pg.completion_pct || 0} className="h-1.5" />
          <div className="flex flex-wrap gap-1.5 pt-1">
            <Badge variant="outline" className="rounded-full text-[10px] border-amber-500/40 bg-amber-500/10 text-amber-600">
              {t("milestones.status.doing", { defaultValue: "In progress" })} {pg.milestones_doing ?? 0}
            </Badge>
            <Badge variant="outline" className="rounded-full text-[10px] border-primary/40 bg-primary/10 text-primary">
              {t("milestones.status.done", { defaultValue: "Done" })} {pg.milestones_done ?? 0}
            </Badge>
            <Badge
              variant="outline"
              className={`rounded-full text-[10px] ${pg.milestones_overdue > 0 ? "border-rose-500/40 bg-rose-500/10 text-rose-600" : ""}`}
            >
              {t("milestones.overdueLabel", { defaultValue: "Overdue" })} {pg.milestones_overdue ?? 0}
            </Badge>
            {pg.enrolled_at && (
              <span className="text-[10px] text-muted-foreground font-mono">{abs(pg.enrolled_at)}</span>
            )}
          </div>
        </div>
        <ul className="space-y-1.5">
          {ms.map((m) => (
            <li key={m.entry_id} className="rounded-xl border border-border p-2.5 flex items-start gap-2" data-testid={`user-view-milestone-${m.entry_id}`}>
              <span className="min-w-0 flex-1">
                <span className={`block text-xs ${m.status === "done" ? "line-through text-muted-foreground" : "font-medium"}`}>{m.title}</span>
                <span className="block text-[11px] text-muted-foreground font-mono">
                  {m.stage ? `${m.stage.replace(/_/g, " ")} · ` : ""}{m.due_at || "—"}
                </span>
                {m.note && <span className="block text-[11px] text-muted-foreground mt-0.5">{m.note}</span>}
              </span>
              <Badge variant="outline" className="text-[10px] shrink-0">
                {t(`milestones.status.${m.status}`, { defaultValue: m.status })}
              </Badge>
            </li>
          ))}
        </ul>
      </div>
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent aria-describedby={undefined} className="w-[calc(100vw-2rem)] sm:w-full max-w-3xl rounded-2xl" data-testid="admin-user-activity-dialog">
        <DialogHeader>
          <DialogTitle className="font-display truncate">
            {user?.name || user?.email || t("adminUserView.title", { defaultValue: "User" })}
          </DialogTitle>
        </DialogHeader>

        {(data?.user?.phone || data?.user?.email) && (
          <div className="flex flex-wrap items-center gap-3 rounded-xl border border-border p-2.5 text-[11px]">
            <span className="text-muted-foreground">{data.user.email}</span>
            {data.user.phone && (
              <span className="font-mono" dir="ltr" data-testid="user-activity-phone">{data.user.phone}</span>
            )}
            {bt && (
              <span className="inline-flex items-center gap-1 rounded-full bg-sky-500/10 text-sky-700 border border-sky-500/25 px-2 py-0.5 font-medium" data-testid="user-activity-business-type">
                {bt.name_en}
                {user?.journey_template_count > 0 && <span className="font-mono opacity-70">· {user.journey_template_count}</span>}
              </span>
            )}
            <span className="ms-auto">{t(`roles.${data.user.role}`, { defaultValue: data.user.role })}</span>
          </div>
        )}

        <div className="flex gap-1.5 flex-wrap">
          {TABS.map((key) => {
            const Icon = icons[key];
            const count = counts?.[key];
            return (
              <button
                key={key}
                type="button"
                onClick={() => setTab(key)}
                className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-medium border transition-colors ${
                  tab === key
                    ? "bg-primary text-primary-foreground border-primary"
                    : "bg-background text-muted-foreground border-border hover:text-primary hover:border-primary/40"
                }`}
                data-testid={`user-activity-tab-${key}`}
              >
                <Icon className="h-3.5 w-3.5" />
                {labels[key]}
                {typeof count === "number" && <span className="font-mono opacity-70">{count}</span>}
              </button>
            );
          })}
        </div>

        {loading ? (
          <div className="flex justify-center py-10"><Loader2 className="h-5 w-5 animate-spin text-primary" /></div>
        ) : tab === "overview" ? (
          renderOverview()
        ) : tab === "edit" ? (
          <AdminUserEditForm detail={detail} businessTypes={businessTypes} onSaved={load} />
        ) : tab === "profile" ? (
          renderProfile()
        ) : tab === "program" ? (
          renderProgram()
        ) : MEMBER_TABS.includes(tab) ? (
          renderMemberRows(tab)
        ) : !data ? (
          <p className="text-sm text-muted-foreground py-6 text-center">
            {t("adminActivity.noTimeline", { defaultValue: "No activity recorded for this account yet." })}
          </p>
        ) : tab === "sign_ins" ? (
          <ul className="space-y-2 max-h-[50vh] overflow-y-auto" data-testid="user-activity-signins">
            {(data.sign_ins || []).length === 0 ? (
              <p className="text-sm text-muted-foreground py-4 text-center">
                {t("adminActivity.noSignIns", { defaultValue: "No sign-ins recorded." })}
              </p>
            ) : (
              data.sign_ins.map((s) => (
                <li key={s.log_id} className="rounded-xl border border-border p-2.5" data-testid={`user-activity-signin-${s.log_id}`}>
                  <div className="flex items-center justify-between gap-2">
                    <p className="text-xs font-medium">{rel(s.created_at)}</p>
                    <Badge variant="outline" className="text-[10px]">{s.method === "google_oauth" ? "Google" : "QA"}</Badge>
                  </div>
                  <p className="text-[11px] text-muted-foreground mt-0.5 flex items-center gap-1.5 flex-wrap">
                    <MapPin className="h-3 w-3" />
                    {[s.city, s.country].filter(Boolean).join(", ") || "—"} · {s.browser} / {s.os} · <code className="font-mono">{s.ip || "—"}</code>
                  </p>
                  <p className="text-[10px] text-muted-foreground font-mono mt-0.5">{abs(s.created_at)}</p>
                </li>
              ))
            )}
          </ul>
        ) : (
          <ul className="space-y-1 max-h-[50vh] overflow-y-auto" data-testid={`user-activity-${tab}`}>
            {(data[tab] || []).length === 0 ? (
              <p className="text-sm text-muted-foreground py-4 text-center">
                {t("adminActivity.noChanges", { defaultValue: "Nothing recorded here yet." })}
              </p>
            ) : (
              data[tab].map((row) => <AuditRow key={row.audit_id} row={row} t={t} />)
            )}
          </ul>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default AdminUserActivityDialog;
